/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * LeaderboardPage — /results/:eventActivityId
 *
 * The motion board: one race's results. Public, so a visitor with the link sees
 * the field without signing in; a signed-in athlete also gets their own row
 * pinned to the foot of the table.
 *
 * Direction A from the design canvas, built bottom-up: this is the field and
 * the tabs. The podium, the hero and the personal band read PUBLISHED awards,
 * which do not exist until the publish phase, so they are not here yet.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useFeedUser } from '../useFeedData';
// Shared with the index, so the app token is minted once — see boardClient.
import boardClient from './boardClient';
// The other activities of the same event, so the board is a way into all of
// them rather than a dead end on one. Public reads, same as the board itself.
import { fetchEventActivities } from './boardIndexApi';
import { idOf } from './boardIndexModel';
import {
  fetchWholeTab, BOARD_MAX_ROWS, fetchStatusCounts, fetchMyResultForRace, fetchFieldSummary, countsFromSummary,
  fetchPodium, fetchFinishBands,
} from './boardApi';
// Attaching happens here, on the board, not in a separate organizer section —
// and the panel draws nothing unless the viewer created this race. See its own
// header: the server decides, not this page.
import OrganizerAttachPanel from './OrganizerAttachPanel';
import OrganizerBoardLink from './OrganizerBoardLink';
// Switching tabs used to show the words "Loading results…", which read as the
// page having stopped. This keeps the table's shape on screen and moves.
import BoardLoader from './BoardLoader';
import {
  TABS, DEFAULT_TAB, visibleTabs, sortingAllowedOn, defaultSortFor, initialDirectionFor,
  emptyCopyFor, formatClock, formatKm, formatPace, sortRows, percentBehind,
  histogramBars, HISTOGRAM_MIN_FINISHERS,
} from './boardModel';

const apiHelper = boardClient;

/** How long each podium card holds the phone screen before the next slides in. */
const PODIUM_DWELL_MS = 3600;

/** Below this the podium is a swipe rail; above it, the three-across podium. */
const PHONE = '(max-width: 760px)';

/**
 * The podium rail that turns itself, on phones only.
 *
 * WHY A HOOK AND NOT CSS. A scroll-snap rail gives the swipe for free, but
 * nothing in CSS can advance it, pause it on a touch or draw a timer. The
 * three things have to move together or the bar lies about when the card
 * turns.
 *
 * IT STOPS WHEN TOUCHED, and that is the whole point of it. Hover, a drag, or
 * the keyboard reaching a card all hold it, because a card that slides away
 * mid-read is worse than one that never moved.
 *
 * REDUCED MOTION GETS NO ROTATION AT ALL. A carousel is the one thing a person
 * cannot opt out of by simply not touching it, so when the phone asks for less
 * motion the rail sits still, the bar sits full on the current card, and the
 * cards are reached by swiping. This is also why the rotation must never be the
 * ONLY way to see second and third.
 *
 * The hook is inert on desktop: `enabled` is the media query, so the effect
 * tears down the moment the layout goes back to three-across.
 */
function usePodiumRotation() {
  // A CALLBACK REF, NOT useRef. The podium only renders once BOTH the awards
  // and the results have arrived, which is later than the last time the deps
  // of this effect change — so with a plain ref the effect ran while
  // `railRef.current` was still null, bailed out, and never ran again. The
  // rail then sat there swipeable but dead. Setting state when the node
  // attaches is what re-runs the effect at the only moment it can work.
  const [rail, setRail] = useState(null);
  const railRef = useCallback((node) => setRail(node), []);
  const [onPhone, setOnPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(PHONE).matches : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(PHONE);
    const sync = () => setOnPhone(mq.matches);
    sync();
    // addListener is the old name, still the only one some in-app browsers have.
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    };
  }, []);

  useEffect(() => {
    if (!rail || !onPhone) return undefined;

    const cards = Array.from(rail.children);
    const fills = cards.map((c) => c.querySelector('[data-podium-fill]'));
    // One winner is not a carousel, and two are reached in a single swipe.
    if (cards.length < 2) return undefined;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let index = 0;
    let elapsed = 0;
    let held = false;
    let last = 0;
    let frame = 0;

    const hold = (on) => { held = on; };
    const slideTo = (n) => {
      index = ((n % cards.length) + cards.length) % cards.length;
      rail.scrollTo({
        left: cards[index].offsetLeft - cards[0].offsetLeft,
        behavior: still ? 'auto' : 'smooth',
      });
    };

    // A swipe of their own takes over: whatever card they land on becomes the
    // current one and its timer starts again from nothing, so it never turns
    // the instant they let go.
    const onScroll = () => {
      const x = rail.scrollLeft + cards[0].offsetLeft;
      let best = 0;
      let near = Infinity;
      cards.forEach((c, n) => {
        const d = Math.abs(c.offsetLeft - x);
        if (d < near) { near = d; best = n; }
      });
      if (best !== index) { index = best; elapsed = 0; }
    };

    const tick = (t) => {
      if (!last) last = t;
      const dt = t - last;
      last = t;
      if (!held && !still) {
        elapsed += dt;
        if (elapsed >= PODIUM_DWELL_MS) { elapsed = 0; slideTo(index + 1); }
      }
      const ratio = still ? 1 : Math.min(elapsed / PODIUM_DWELL_MS, 1);
      fills.forEach((f, n) => {
        if (f) f.style.transform = `scaleX(${n === index ? ratio : 0})`;
      });
      frame = window.requestAnimationFrame(tick);
    };

    const enter = () => hold(true);
    const leave = () => hold(false);
    rail.addEventListener('mouseenter', enter);
    rail.addEventListener('mouseleave', leave);
    rail.addEventListener('pointerdown', enter);
    rail.addEventListener('focusin', enter);
    rail.addEventListener('focusout', leave);
    window.addEventListener('pointerup', leave);
    window.addEventListener('pointercancel', leave);
    rail.addEventListener('scroll', onScroll, { passive: true });
    frame = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(frame);
      rail.removeEventListener('mouseenter', enter);
      rail.removeEventListener('mouseleave', leave);
      rail.removeEventListener('pointerdown', enter);
      rail.removeEventListener('focusin', enter);
      rail.removeEventListener('focusout', leave);
      window.removeEventListener('pointerup', leave);
      window.removeEventListener('pointercancel', leave);
      rail.removeEventListener('scroll', onScroll);
      // Leaving the phone layout must not leave a half-drawn bar behind.
      fills.forEach((f) => { if (f) f.style.transform = 'scaleX(0)'; });
    };
  }, [rail, onPhone]);

  return { railRef, onPhone };
}

const INK = '#241708';
const MUTED = '#6B5844';
const ACCENT = '#C2410C';
/** The brand orange, used as a FILL (bars, steps, rings) — never for small text. */
const FLAME = '#FF6A1A';
const SOFT = '#FFF4EC';
const HAIR = 'rgba(36, 23, 8, .10)';
const DISPLAY = "Archivo, Inter, system-ui, sans-serif";
const ROWS_PER_PAGE = 100;

/** The podium is named rather than given a percentage. */
const PODIUM_WORD = { 1: 'Winner', 2: 'Runner-up', 3: 'Third place' };

/** Every card on the board — the hero aside — sits on the same white plate. */
const card = {
  background: '#fff', border: `1px solid ${HAIR}`, borderRadius: 24,
  boxShadow: '0 1px 2px rgba(36,23,8,.04), 0 12px 32px -20px rgba(36,23,8,.18)',
};
const th = {
  textAlign: 'left', padding: '0 14px', height: 46, fontSize: 11, fontWeight: 700,
  letterSpacing: '.12em', textTransform: 'uppercase', color: MUTED, background: '#FBF8F5',
  borderTop: `1px solid ${HAIR}`, borderBottom: `1px solid ${HAIR}`, whiteSpace: 'nowrap',
};
const td = { padding: '0 14px', height: 64, borderBottom: '1px solid rgba(36,23,8,.06)' };
const mono = { fontVariantNumeric: 'tabular-nums' };
const srOnly = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' };

/** "Priya Sharma" -> "PS". One letter when there is only one word. */
function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/** 16540 -> "16,540". Anything that is not a number is shown as it came. */
function countOf(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString('en-IN') : String(n ?? '');
}

/** 1 -> "1st", 2 -> "2nd", 11 -> "11th". */
function ordinal(n) {
  const v = Number(n);
  const tens = v % 100;
  if (tens >= 11 && tens <= 13) return `${v}th`;
  return `${v}${({ 1: 'st', 2: 'nd', 3: 'rd' })[v % 10] || 'th'}`;
}

/**
 * Race names arrive as "10K Run - Timed Category with RFID Chip, Finisher
 * Medal…". Set whole in the hero that was four uppercase lines; the part
 * before the first " - " is the name a runner actually says, and the rest
 * reads better as the line beneath it. Nothing is dropped — only split.
 */
function splitRaceName(name) {
  const text = String(name || '').trim();
  const at = text.indexOf(' - ');
  if (at <= 0) return [text, ''];
  return [text.slice(0, at).trim(), text.slice(at + 3).trim()];
}

/**
 * The hero's art, one group scene per activity type, in
 * public/images/results-hero/ (built from public-domain silhouettes by
 * scripts/results-hero-art — see its SOURCES.md). Every file is flat grey on
 * transparency, so the one CSS mask below recolours any of them.
 *
 * Every file is the same HEIGHT and as wide as its scene needs, so each one
 * stands as tall in the hero as the runners do. The number is that file's
 * width / height, which the hero box needs to take the scene's shape; the
 * script prints these whenever it rebuilds the art.
 */
const HERO_ART = {
  RUN: ['run', 1.201], WALK: ['walk', 1.408], HIKE: ['hike', 1.259], CYCLING: ['cycling', 1.774],
  RIDE: ['ride', 2.311], SWIM: ['swim', 1.702], YOGA: ['yoga', 1.508], DRIVE: ['drive', 1.53],
};

/**
 * For an activity type with no scene of its own — one added after these were
 * drawn, or a race whose type cannot be told. Not any one sport: a walker, a
 * runner and a cyclist together, so a new type never wears another's picture.
 */
const HERO_ART_DEFAULT = ['default', 1.593];

/**
 * Words in a race's name that give its type away, for a server whose field
 * summary does not carry `activity_type_code` yet. Most specific first:
 * "5K Heritage Walk" is a walk, not a run, and "40K Cycling Tour … Support
 * Car" is a ride, not a drive.
 */
const TYPE_WORDS = [
  ['SWIM', /\bswim/i],
  ['YOGA', /\byoga\b/i],
  ['CYCLING', /\bcycl/i],
  ['RIDE', /\b(ride|bike|biking|mtb|moto|motorcycle|motorbike|motocross|enduro)\b/i],
  ['HIKE', /\b(hike|hiking|trek)/i],
  ['WALK', /\bwalk/i],
  ['DRIVE', /\b(drive|rally|car)\b/i],
  ['RUN', /\b(run|running|marathon|jog|\d+\s?k)\b/i],
];

/**
 * The hero art for a race — by its type code, else its name, else the
 * default scene — as { src, aspect }.
 *
 * A code the page does not know (a type added later) falls through to the
 * name, and only then to the default: "Sunday Trail Run" under a new
 * TRAIL_RUN type still gets runners rather than the generic scene.
 */
function heroArtFor(code, raceName) {
  let key = String(code || '').toUpperCase();
  if (!HERO_ART[key]) {
    const hit = TYPE_WORDS.find(([, words]) => words.test(raceName || ''));
    key = hit ? hit[0] : '';
  }
  const [file, aspect] = HERO_ART[key] || HERO_ART_DEFAULT;
  return { src: `/images/results-hero/${file}.png`, aspect };
}

/**
 * The page buttons to draw: both ends, and the current page with its
 * neighbours. `null` marks a gap, drawn as an ellipsis.
 */
function pageWindow(current, count) {
  const keep = new Set([1, count, current - 1, current, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => keep.add(n));
  if (current >= count - 2) [count - 1, count - 2, count - 3].forEach((n) => keep.add(n));
  const pages = [...keep].filter((n) => n >= 1 && n <= count).sort((a, b) => a - b);
  const out = [];
  pages.forEach((n, i) => {
    if (i && n - pages[i - 1] > 1) out.push(null);
    out.push(n);
  });
  return out;
}

/** Where a finish time falls along the chart, as a percentage from the left, or null. */
function positionOf(seconds, bars) {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0 || bars.length === 0) return null;
  const i = bars.findIndex((b) => s >= b.startSeconds && s < b.endSeconds);
  if (i < 0) return null;
  const b = bars[i];
  return ((i + (s - b.startSeconds) / (b.endSeconds - b.startSeconds)) / bars.length) * 100;
}

/** "12.3%" of the field; under 0.1% reads "<0.1%" rather than a misleading zero. */
function shareText(runners, total) {
  if (!total) return '';
  const pct = (runners / total) * 100;
  if (pct > 0 && pct < 0.1) return '<0.1%';
  return `${pct >= 10 ? Math.round(pct) : pct.toFixed(1)}%`;
}

/**
 * The shape of the field: how many finishers landed in each slice of time.
 *
 * Drawn as plain boxes rather than a charting library — the board already
 * loads on a phone over event wifi, and one more bundle to show thirty
 * rectangles is a poor trade.
 *
 * Every bar is a BUTTON. Hover, focus or a tap opens its figures above it —
 * a tap because a phone has no hover. Each button also carries the same
 * figures in its label, so the shape is available to a reader who cannot
 * see it as well as to one who can.
 */
function FinishSpread({ bars, mineResult, summary }) {
  // Which bar's figures are open; null for none. Purely visual.
  const [active, setActive] = useState(null);
  // What last pressed a bar. A tap on a phone also fires the mouse-enter and
  // focus a mouse would — four handlers each setting the bar, and blur
  // clearing it again — which is what made the figures flicker and close.
  // So the kind of pointer decides: a MOUSE opens a bar by hovering and
  // closes it by leaving the chart; a TOUCH opens it with a tap, a second
  // tap on it (or a tap anywhere off the chart) closes it.
  const pointerKind = useRef('mouse');
  const chartRef = useRef(null);

  useEffect(() => {
    if (active === null) return undefined;
    const away = (e) => {
      if (chartRef.current && !chartRef.current.contains(e.target)) setActive(null);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [active]);
  if (!bars || bars.length === 0) return null;
  const busiest = bars.reduce((best, b) => (b.runners > best.runners ? b : best), bars[0]);
  const mine = bars.find((b) => b.mine);
  const ahead = mineResult && summary
    ? percentBehind(mineResult.position_overall, summary.completed)
    : null;
  const field = bars.reduce((sum, b) => sum + b.runners, 0);

  // Five labels along the foot, evenly spaced across the bands — the design's
  // axis. With fewer bars than labels it simply repeats the nearest band.
  const marks = [0, 0.25, 0.5, 0.75, 1]
    .map((at) => bars[Math.min(bars.length - 1, Math.round(at * (bars.length - 1)))])
    .filter(Boolean);

  const medianAt = positionOf(summary?.median_seconds, bars);
  const mineAt = mineResult ? positionOf(mineResult.duration_seconds, bars) : null;
  const place = Number(mineResult?.position_overall);
  // Past two-thirds of the way across, the callout hangs to the LEFT of its
  // line, or it runs off the card.
  const flip = mineAt !== null && mineAt > 66;
  // The median's tag drops below the callout when the two would collide.
  const medianLow = medianAt !== null && mineAt !== null && Math.abs(medianAt - mineAt) < 22;

  const open = active !== null ? bars[active] : null;
  const openAt = active !== null ? ((active + 0.5) / bars.length) * 100 : 0;
  // The popup is centred on its bar, except near either edge where it would
  // be clipped by the card.
  const openShift = openAt < 14 ? '-12%' : (openAt > 86 ? '-88%' : '-50%');
  // Over a tall bar there is no room above it — the popup would ride up into
  // the heading — so it sits BESIDE the bar instead, on whichever side has room.
  const openTall = Boolean(open && open.share > 0.55);
  const openRight = openAt < 60;
  const openPlace = openTall
    ? {
      '--tx': openRight ? '0%' : '-100%',
      top: 8,
      left: openRight
        ? `calc(${((active + 1) / bars.length) * 100}% + 8px)`
        : `calc(${(active / bars.length) * 100}% - 8px)`,
    }
    : {
      '--tx': openShift,
      left: `${openAt}%`,
      bottom: open ? `calc(${Math.max(3, open.share * 90)}% + 12px)` : 0,
    };

  return (
    <section
      aria-label="Spread of finish times"
      className="mb-spread"
      style={{ ...card, padding: '26px 28px 22px', marginBottom: 22, position: 'relative' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontFamily: DISPLAY, fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', color: INK }}>
            {ahead !== null ? (
              <>You finished ahead of <span style={{ color: ACCENT }}>{ahead}%</span> of the field</>
            ) : 'How the field finished'}
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: MUTED }}>
            Every finish time, in five-minute bands
            {mine && <> — yours is the {formatClock(mine.startSeconds)} band</>}
          </p>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: MUTED }}>Hover or tap a bar for its figures</p>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 30 }}>
        <div className="mb-spread-y" aria-hidden="true" style={{ ...mono, position: 'relative', width: 40, flex: 'none', height: 240, fontSize: 11, color: MUTED, textAlign: 'right' }}>
          <span style={{ position: 'absolute', right: 0, bottom: -7 }}>0</span>
          <span style={{ position: 'absolute', right: 0, bottom: 'calc(45% - 7px)' }}>{countOf(Math.round(busiest.runners / 2))}</span>
          <span style={{ position: 'absolute', right: 0, bottom: 'calc(90% - 7px)' }}>{countOf(busiest.runners)}</span>
        </div>

        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <div
            style={{ position: 'relative', height: 240, borderBottom: '1px solid rgba(36,23,8,.16)' }}
            ref={chartRef}
            onPointerLeave={(e) => { if (e.pointerType === 'mouse') setActive(null); }}
          >
            <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: '45%', borderTop: '1px dashed rgba(36,23,8,.10)' }} />
            <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: '90%', borderTop: '1px dashed rgba(36,23,8,.10)' }} />

            {medianAt !== null && (
              <span aria-hidden="true" style={{ position: 'absolute', left: `${medianAt}%`, top: 0, bottom: 0, borderLeft: `2px dashed ${INK}`, opacity: 0.55, pointerEvents: 'none' }} />
            )}

            <div
              className={`mb-bars${active !== null ? ' is-active' : ''}`}
              style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: `repeat(${bars.length}, minmax(0, 1fr))`, alignItems: 'end' }}
            >
              {bars.map((bar, i) => (
                <button
                  key={bar.startSeconds}
                  type="button"
                  className="mb-barbtn"
                  aria-label={`${formatClock(bar.startSeconds)} to ${formatClock(bar.endSeconds)}: ${bar.runners} ${bar.runners === 1 ? 'finisher' : 'finishers'}${bar.mine ? ', including you' : ''}`}
                  aria-pressed={active === i}
                  onPointerDown={(e) => { pointerKind.current = e.pointerType; }}
                  onPointerEnter={(e) => { if (e.pointerType === 'mouse') setActive(i); }}
                  // Keyboard only: a tap focuses too, and is handled by click.
                  onKeyUp={(e) => { if (e.key === 'Tab') setActive(i); }}
                  onClick={() => {
                    if (pointerKind.current === 'mouse') setActive(i);
                    else setActive((cur) => (cur === i ? null : i));
                    pointerKind.current = 'mouse';
                  }}
                  style={{
                    height: '100%', padding: '0 2px', border: 0, background: 'none', margin: 0,
                    display: 'flex', alignItems: 'flex-end', boxSizing: 'border-box', minWidth: 0,
                  }}
                >
                  <span
                    className={`mb-bar${active === i ? ' is-on' : ''}`}
                    style={{
                      display: 'block', width: '100%',
                      // A bar that is there but short must still be visible, or an
                      // empty slot and a lone finisher look identical.
                      height: bar.runners > 0 ? `${Math.max(3, bar.share * 90)}%` : 2,
                      borderRadius: '6px 6px 2px 2px',
                      background: bar.mine
                        ? `linear-gradient(180deg, #E2581A 0%, ${ACCENT} 100%)`
                        : (bar.runners > 0 ? 'linear-gradient(180deg, #FF9A5C 0%, #FF6A1A 100%)' : HAIR),
                      animationDelay: `${Math.min(i * 28, 700)}ms`,
                    }}
                  />
                </button>
              ))}
            </div>

            {medianAt !== null && (
              <span
                aria-hidden="true"
                style={{
                  ...mono, position: 'absolute', left: `${medianAt}%`, top: medianLow ? 64 : -4, pointerEvents: 'none',
                  opacity: open ? 0.15 : 1, transition: 'opacity .2s ease',
                  transform: 'translateX(8px)', fontSize: 12, fontWeight: 700, color: INK, whiteSpace: 'nowrap',
                  background: '#fff', padding: '2px 7px', borderRadius: 6, border: `1px solid ${HAIR}`,
                }}
              >
                Median {formatClock(summary.median_seconds)}
              </span>
            )}

            {mineAt !== null && (
              <div
                aria-hidden="true"
                className="mb-callout"
                style={{
                  position: 'absolute', left: `${mineAt}%`, top: 0, pointerEvents: 'none',
                  transform: flip ? 'translateX(calc(-100% + 14px))' : 'translateX(-14px)',
                  background: INK, color: '#fff', borderRadius: 12, padding: '8px 12px', whiteSpace: 'nowrap',
                  boxShadow: '0 10px 24px -10px rgba(36,23,8,.5)',
                  opacity: open && !open.mine ? 0.15 : 1,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', color: '#FFB48A' }}>
                  YOU{place >= 1 ? ` · ${ordinal(place)}` : ''}
                </div>
                <div style={{ ...mono, marginTop: 2, fontSize: 14, fontWeight: 700 }}>
                  {formatClock(mineResult.duration_seconds)}
                </div>
              </div>
            )}

            {/* The open bar's figures. Keyed on the bar so moving to another
                one replays the pop rather than sliding the old box across. */}
            {open && (
              <div
                key={open.startSeconds}
                aria-hidden="true"
                className="mb-tip"
                style={{
                  ...openPlace,
                  position: 'absolute',
                  zIndex: 2, pointerEvents: 'none', minWidth: 168,
                  background: '#fff', border: `1px solid ${HAIR}`, borderRadius: 14, padding: '10px 14px',
                  boxShadow: '0 18px 40px -18px rgba(36,23,8,.45)', whiteSpace: 'nowrap',
                }}
              >
                <div style={{ ...mono, fontSize: 12, fontWeight: 700, color: MUTED }}>
                  {formatClock(open.startSeconds)} – {formatClock(open.endSeconds)}
                </div>
                <div style={{ marginTop: 4, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ ...mono, fontFamily: DISPLAY, fontSize: 24, fontWeight: 800, lineHeight: 1, color: INK }}>
                    {countOf(open.runners)}
                  </span>
                  <span style={{ fontSize: 13, color: MUTED }}>{open.runners === 1 ? 'finisher' : 'finishers'}</span>
                </div>
                <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ ...mono, fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: SOFT, color: ACCENT }}>
                    {shareText(open.runners, field)} of the field
                  </span>
                  {open.mine && (
                    <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 6, background: INK, color: '#fff' }}>YOU</span>
                  )}
                  {open === busiest && busiest.runners > 1 && (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: '#F6F2EE', color: INK }}>Busiest</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div aria-hidden="true" style={{ ...mono, display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, fontWeight: 500, color: MUTED }}>
            {marks.map((bar, i) => <span key={i}>{formatClock(bar.startSeconds)}</span>)}
          </div>
        </div>
      </div>

      <div aria-hidden="true" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 16, fontSize: 13, color: '#4A3D33' }}>
        {mine && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: ACCENT }} />
            Your band · {countOf(mine.runners)} {mine.runners === 1 ? 'finisher' : 'finishers'}
          </span>
        )}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: FLAME }} />
          {mine ? 'Other bands' : 'Finishers per band'}
        </span>
        {medianAt !== null && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ height: 14, borderLeft: `2px dashed ${INK}`, opacity: 0.55 }} />Median
          </span>
        )}
      </div>

      {busiest.runners > 1 && (
        <p style={srOnly}>Most finishers came in around {formatClock(busiest.startSeconds)}.</p>
      )}
    </section>
  );
}

export default function LeaderboardPage() {
  const { eventActivityId } = useParams();
  const raceId = Number(eventActivityId);
  const user = useFeedUser();

  const [tabKey, setTabKey] = useState(DEFAULT_TAB);
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);

  const tab = TABS.find((t) => t.key === tabKey) || TABS[0];

  // One saved query carries every tab count AND the median, which no ordinary
  // read can give: a median needs every finish time sorted, not one page of
  // them. If that query is not seeded on this server the counts still work —
  // they fall back to one small read per tab, since every read answers with a
  // total anyway.
  const counts = useQuery({
    queryKey: ['board', 'summary', raceId],
    queryFn: async ({ signal }) => {
      try {
        const row = await fetchFieldSummary(apiHelper, { eventActivityId: raceId, signal });
        return { counts: countsFromSummary(row), summary: row };
      } catch (e) {
        const tallies = await fetchStatusCounts(apiHelper, { eventActivityId: raceId, tabs: TABS, signal });
        return { counts: tallies, summary: null };
      }
    },
    enabled: Number.isFinite(raceId),
    // One retry, as with the rows. The default three held the title in its
    // skeleton long after the finishers had arrived — the page looked stuck on
    // its own name while showing a complete table underneath.
    retry: 1,
  });
  const tabCounts = counts.data?.counts || {};
  const summary = counts.data?.summary || null;

  const results = useQuery({
    queryKey: ['board', 'rows', raceId, tabKey, term],
    queryFn: ({ signal }) => fetchWholeTab(apiHelper, {
      eventActivityId: raceId, statuses: tab.statuses, search: term || undefined, signal,
    }),
    enabled: Number.isFinite(raceId),
    // One retry, not the default three. Three kept the page "loading" for the
    // best part of ten seconds before it would admit anything was wrong, and a
    // visitor reads that as a hang rather than a failure.
    retry: 1,
  });

  const podium = useQuery({
    queryKey: ['board', 'podium', raceId],
    queryFn: ({ signal }) => fetchPodium(apiHelper, { eventActivityId: raceId, signal }),
    enabled: Number.isFinite(raceId),
  });
  // Every race, so this one's siblings can be found. One cached read shared
  // with the /results index — moving between boards does not re-ask.
  const races = useQuery({
    queryKey: ['board-index', 'event-activities'],
    queryFn: ({ signal }) => fetchEventActivities(apiHelper, { signal }),
    staleTime: 120000,
  });

  const bands = useQuery({
    queryKey: ['board', 'bands', raceId],
    queryFn: ({ signal }) => fetchFinishBands(apiHelper, { eventActivityId: raceId, signal }),
    enabled: Number.isFinite(raceId),
  });
  const mine = useQuery({
    queryKey: ['board', 'mine', raceId, user.userId],
    queryFn: ({ signal }) => fetchMyResultForRace(apiHelper, {
      userId: user.userId, eventActivityId: raceId, signal,
    }),
    enabled: Number.isFinite(raceId) && user.userId !== null,
  });

  // The default follows the race: time for a set distance, distance for an open
  // challenge. Until a heading is clicked, that is what the board is sorted by.
  const activeSort = sort || defaultSortFor({ total_distance: 1 });
  const canSort = sortingAllowedOn(tabKey);

  const rows = results.data?.rows || [];
  const sorted = useMemo(
    () => (canSort ? sortRows(rows, activeSort.column, activeSort.direction) : rows),
    [rows, canSort, activeSort.column, activeSort.direction],
  );
  const pageCount = Math.max(1, Math.ceil(sorted.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const visible = sorted.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);

  // This race's event, then its siblings in running order.
  const siblings = useMemo(() => {
    const all = races.data?.races || [];
    const here = all.find((r) => String(r.id) === String(raceId));
    const eventId = here ? idOf(here.event_id) : null;
    if (eventId === null || eventId === undefined) return [];
    return all
      .filter((r) => String(idOf(r.event_id)) === String(eventId))
      .sort((a, b) => Number(a.order_index ?? a.id) - Number(b.order_index ?? b.id));
  }, [races.data, raceId]);

  // On a phone the podium is a rail that turns itself; on desktop this is
  // inert and the three cards stand as they always have.
  const { railRef } = usePodiumRotation();

  // The podium, with each winner's own result joined back on so the card can
  // carry a time and a pace. The award table freezes names, not figures.
  const podiumCards = useMemo(() => {
    const byResult = new Map((results.data?.rows || []).map((r) => [String(r.id), r]));
    return (podium.data || [])
      .map((award) => ({ award, result: byResult.get(String(idOf(award.event_result_id))) || null }))
      .sort((a, b) => Number(a.award.position) - Number(b.award.position));
  }, [podium.data, results.data]);

  // How much of the field is behind you, or null when the race is not ranked.
  const ahead = mine.data && summary
    ? percentBehind(mine.data.position_overall, summary.completed)
    : null;

  // The podium column lifted by a tap (hover needs no state). Purely visual.
  const [podiumOn, setPodiumOn] = useState(null);

  const tabs = visibleTabs(tabCounts);
  const empty = emptyCopyFor(tabKey);
  const [raceTitle, raceDetail] = splitRaceName(summary?.race_name);
  // Held back until the summary has settled, so a swim never flashes up as a
  // pack of runners for a beat while the race's type is still on its way.
  const heroArt = counts.isPending ? null : heroArtFor(summary?.activity_type_code, summary?.race_name);
  const myPlace = Number(mine.data?.position_overall);

  function pickTab(key) {
    setTabKey(key);
    setPage(1);
    setSort(null);
  }

  function pickSort(column) {
    setPage(1);
    setSort((current) => (current && current.column === column
      ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { column, direction: initialDirectionFor(column) }));
  }

  function onSearch(event) {
    event.preventDefault();
    setTerm(search.trim());
    setPage(1);
  }

  function toMyResult() {
    const node = document.getElementById('my-result');
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  if (!Number.isFinite(raceId)) {
    return <main style={{ padding: 32 }}><p style={{ color: MUTED }}>That race link is not valid.</p></main>;
  }

  return (
    <main style={{ maxWidth: 1160, margin: '0 auto', padding: '28px 20px 80px' }}>
      <style>
        {`.mb-tabstrip::-webkit-scrollbar{display:none}
          @keyframes mb-skel-sweep { from { background-position: -240px 0 } to { background-position: 460px 0 } }
          .mb-skel {
            background-color: rgba(250,243,231,.10);
            background-image: linear-gradient(90deg, rgba(250,243,231,0) 0%, rgba(250,243,231,.20) 45%, rgba(250,243,231,0) 70%);
            background-size: 400px 100%; background-repeat: no-repeat;
            animation: mb-skel-sweep 1.3s linear infinite;
          }
          @media (prefers-reduced-motion: reduce) { .mb-skel { animation: none } }
          .mb-podium-track { display: none }
          .mb-row:hover td { background: #FFFBF8 }

          /* ── Podium motion ──────────────────────────────────────────
             The steps rise into place, second then third then the champion
             last, and the people on them settle in after. Hover (or a tap,
             which sets .is-on) lifts a column: the step grows from the floor
             and the runner rises with it — the step never leaves the floor. */
          @keyframes mb-rise { from { transform: scaleY(0) } to { transform: scaleY(1) } }
          @keyframes mb-pop { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: none } }
          @keyframes mb-crown { 0%, 100% { transform: translateY(0) rotate(0) } 50% { transform: translateY(-4px) rotate(-6deg) } }
          .mb-pod { cursor: pointer; -webkit-tap-highlight-color: transparent }
          .mb-step { transform-origin: bottom center; animation: mb-rise .7s cubic-bezier(.2,.8,.2,1) both;
            transition: transform .35s cubic-bezier(.2,.8,.2,1), box-shadow .35s ease, filter .35s ease }
          .mb-pod-top { animation: mb-pop .6s cubic-bezier(.2,.8,.2,1) both; transition: transform .35s cubic-bezier(.2,.8,.2,1) }
          .mb-pod-ava { transition: transform .35s cubic-bezier(.2,.8,.2,1), box-shadow .35s ease }
          .mb-pod:nth-child(2) .mb-step { animation-delay: .05s } .mb-pod:nth-child(2) .mb-pod-top { animation-delay: .35s }
          .mb-pod:nth-child(3) .mb-step { animation-delay: .15s } .mb-pod:nth-child(3) .mb-pod-top { animation-delay: .45s }
          .mb-pod:nth-child(1) .mb-step { animation-delay: .28s } .mb-pod:nth-child(1) .mb-pod-top { animation-delay: .6s }
          .mb-pod:hover .mb-step, .mb-pod.is-on .mb-step { transform: scaleY(1.08); filter: saturate(1.15); box-shadow: 0 -16px 40px -12px rgba(255,106,26,.55) }
          .mb-pod:hover .mb-pod-top, .mb-pod.is-on .mb-pod-top { transform: translateY(-12px) }
          .mb-pod:hover .mb-pod-ava, .mb-pod.is-on .mb-pod-ava { transform: scale(1.08) }
          .mb-pod:hover .mb-crown, .mb-pod.is-on .mb-crown { animation: mb-crown .8s ease-in-out infinite }

          /* ── Spread chart motion ────────────────────────────────────
             Bars grow from the axis on arrival, left to right; the one under
             the pointer (or tapped) brightens while the rest step back, and
             its figures pop up above it. */
          @keyframes mb-bar { from { transform: scaleY(0) } to { transform: scaleY(1) } }
          @keyframes mb-tip { from { opacity: 0; transform: translate(var(--tx), 8px) scale(.94) } to { opacity: 1; transform: translate(var(--tx), 0) scale(1) } }
          .mb-bar { transform-origin: bottom center; animation: mb-bar .6s cubic-bezier(.2,.8,.2,1) both;
            transition: opacity .2s ease, filter .2s ease, box-shadow .2s ease }
          .mb-bars.is-active .mb-bar { opacity: .38 }
          .mb-bars.is-active .mb-bar.is-on { opacity: 1; filter: saturate(1.2) brightness(1.04); box-shadow: 0 -6px 16px -6px rgba(255,106,26,.7) }
          .mb-barbtn { cursor: pointer; -webkit-tap-highlight-color: transparent }
          .mb-barbtn:focus-visible { outline: 2px solid ${INK}; outline-offset: 2px; border-radius: 6px }
          .mb-tip { animation: mb-tip .22s cubic-bezier(.2,.8,.2,1) both }
          .mb-callout { transition: opacity .2s ease }

          @media (prefers-reduced-motion: reduce) {
            .mb-step, .mb-pod-top, .mb-bar, .mb-tip { animation: none !important }
            .mb-pod .mb-step, .mb-pod .mb-pod-top, .mb-pod .mb-pod-ava { transition: none !important }
            .mb-pod:hover .mb-crown, .mb-pod.is-on .mb-crown { animation: none !important }
          }
          .mb-pg:hover:not(:disabled):not([aria-current]) { border-color: rgba(36,23,8,.28) }
          @media (max-width: 1000px) {
            /* Between tablet and phone the pack stays, but steps back so the
               figures in front of it keep their contrast. */
            .mb-hero-pack { opacity: .5 !important }
          }
          @media (max-width: 760px) {
            /* !important on purpose: these elements carry inline styles, which
               otherwise beat anything a stylesheet says, media query or not. */
            /* The pack sits well back, cropped to the right edge, as texture
               rather than subject — at full strength it fought the numbers. */
            .mb-hero-pack { height: auto !important; width: 62% !important; max-width: 62% !important; max-height: 72% !important; right: 8px !important; bottom: 0 !important; opacity: .42 !important }
            /* THE HERO MUST BE THE TALLEST BLOCK ON THE PAGE, with the words at
               its foot so the extra height never opens as a hole beneath them. */
            .mb-hero { min-height: 260px !important; display: flex !important; flex-direction: column !important; justify-content: flex-end !important; border-radius: 22px !important }
            .mb-hero-pad { padding: 24px 18px 24px !important }
            .mb-hero-pad h1 { font-size: 30px !important }
            /* The four figures as an even two-by-two; in a row they wrapped and
               orphaned the median. */
            .mb-hero-facts { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; gap: 16px 12px !important }
            .mb-hero-facts > div { border-right: 0 !important; padding-right: 0 !important; margin-right: 0 !important }
            .mb-hero-facts .mb-fig-v { font-size: 22px !important }

            /* THE TIME COLUMN MUST BE ON SCREEN. Distance gives way; it is
               already in your result and on the run behind each name. */
            .mb-table { min-width: 0 !important }
            .mb-table th:nth-child(3), .mb-table td:nth-child(3) { display: none }
            .mb-table .mb-avatar { display: none !important }
            .mb-pad { padding-left: 16px !important; padding-right: 16px !important }
            .mb-pg-word { display: none }

            /* The activities scroll sideways rather than stacking into a
               column that pushes the results off the first screen. */
            /* Start-aligned, not centred: a centred row that overflows spills
               off BOTH edges, and the left half — the race you are on — can
               never be scrolled back to. */
            .mb-acts { display: flex !important; flex-wrap: nowrap !important; justify-content: flex-start !important; overflow-x: auto; scrollbar-width: none }
            .mb-acts::-webkit-scrollbar { display: none }

            /* ONE CARD AT A TIME, AND IT TURNS ITSELF. Stacked, three podium
               columns ran most of a screen on their own; as a rail the podium
               costs one card. 78% so the next card peeks with clear air either
               side, which is what tells people it swipes at all. */
            .mb-stage { padding-top: 18px !important; background: none !important; border-bottom: 0 !important }
            .mb-podium {
              display: flex !important; gap: 16px !important; flex-wrap: nowrap !important;
              justify-content: flex-start !important; align-items: stretch !important;
              overflow-x: auto; scroll-snap-type: x mandatory;
              scrollbar-width: none; -webkit-overflow-scrolling: touch;
            }
            .mb-podium::-webkit-scrollbar { display: none }
            /* Each column becomes a card of its own, with a short, even step
               along its foot instead of a tall one. */
            .mb-podium > article {
              flex: 0 0 78% !important; scroll-snap-align: start; overflow: hidden;
              background: #fff; border: 1px solid ${HAIR}; border-radius: 22px; padding-top: 20px;
            }
            /* THE CHAMPION LEADS THE RAIL. Desktop orders the cards 2-1-3 so
               the winner stands in the middle; on a rail the first card is the
               one you land on, and that has to be the winner. */
            .mb-podium > article { order: 0 !important }
            .mb-step { height: 64px !important; border-radius: 0 !important; padding-top: 6px !important; box-shadow: none !important }
            .mb-step > span { font-size: 36px !important }
            /* The timer rides ON the card, along its bottom edge: it says which
               of the three you are on and how long before it turns. */
            .mb-podium-track { display: block !important }

            .mb-yours { padding: 18px !important }
            .mb-tiles { gap: 14px 22px !important }
            .mb-spread { padding: 20px 16px 18px !important }
            .mb-spread-y { display: none !important }
            /* On a phone the bars are a few pixels wide and the figures would
               run off the card beside or above one, so they open as a panel
               across the top of the chart instead — the bar it belongs to
               stays lit underneath. */
            .mb-tip { left: 0 !important; right: 0 !important; top: 0 !important; bottom: auto !important;
              min-width: 0 !important; --tx: 0% !important; white-space: normal !important;
              display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 12px; padding: 8px 12px !important }
            .mb-tip > div { margin-top: 0 !important }
          }`}
      </style>

      {/* Hero — the dark poster from the canvas (C1 · Stage), on a white page.
          The pack of runners is the brand's own art, recoloured to the flame
          gradient with a mask rather than a filter, so it glows instead of
          sitting flat. A scrim keeps the LEFT darkest whatever the art does,
          because that is where the type lands.

          The badge reads results_published on the race: without it, "a fun
          run where nobody placed" and "not published yet" look alike. */}
      <header
        className="mb-hero"
        style={{
          position: 'relative', overflow: 'hidden', borderRadius: 28, marginBottom: 18,
          minHeight: 270, color: '#FFFFFF', backgroundColor: '#1C1007',
          backgroundImage: 'radial-gradient(60% 110% at 82% 60%, rgba(255,106,26,.50) 0%, rgba(255,106,26,.12) 45%, rgba(255,106,26,0) 70%)',
          // Tight and close. The old 60px spread fell onto the activity
          // switcher underneath and greyed it out.
          boxShadow: '0 14px 28px -20px rgba(36,23,8,.45)',
        }}
      >
        {heroArt && (
          <div
            aria-hidden="true"
            className="mb-hero-pack"
            style={{
              // Inside the card on every side: the art carries its own margin,
              // and nothing here pushes it past an edge to be cropped.
              // The box takes the scene's own shape at the card's height, so
              // every activity stands as tall as the runners. A very wide
              // scene is held to 40% of the card — at half, the row of yoga
              // poses ran in under the title — and then shrinks inside the
              // box rather than being cropped.
              position: 'absolute', right: 24, bottom: 0, height: '100%', maxHeight: 300,
              aspectRatio: String(heroArt.aspect), maxWidth: '40%',
              pointerEvents: 'none', opacity: 0.92,
              background: 'linear-gradient(180deg, #FF9A5C 0%, #E2581A 55%, #9A3412 100%)',
              WebkitMask: `url(${heroArt.src}) right bottom / contain no-repeat`,
              mask: `url(${heroArt.src}) right bottom / contain no-repeat`,
            }}
          />
        )}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'linear-gradient(94deg, rgba(28,16,7,.92) 0%, rgba(28,16,7,.70) 38%, rgba(28,16,7,0) 66%), linear-gradient(180deg, rgba(28,16,7,0) 70%, rgba(28,16,7,.55) 100%)',
          }}
        />
        {/* A halftone screen, so the pack reads as printed rather than pasted. */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            backgroundImage: 'radial-gradient(rgba(255,255,255,.08) 1px, transparent 1.3px)',
            backgroundSize: '14px 14px',
          }}
        />

        <div className="mb-hero-pad" style={{ position: 'relative', padding: '26px 34px 26px', maxWidth: 680 }}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, height: 32, padding: '0 14px',
              borderRadius: 999, background: ACCENT, color: '#FFF6E9',
              fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase',
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: 999, background: '#FFF6E9' }} />
            {/* "Results" rather than "Live results" for an unpublished race:
                the distinction that matters is official or not. */}
            {summary?.results_published ? 'Official results' : 'Results'}
          </span>

          {/* The event the race belongs to, read before its name — on ONE line.
              Event names arrive long and repeated; the full text stays in the
              tooltip. */}
          {summary?.event_name && (
            <p
              className="mb-hero-event"
              title={summary.event_name}
              style={{
                margin: '14px 0 0', fontSize: 14, fontWeight: 600, color: '#E8C9B4',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {summary.event_name}
            </p>
          )}

          {/* NO PLACEHOLDER TITLE. A skeleton says "not yet" honestly, and the
              fallback below only appears once the read has actually settled. */}
          <h1
            style={{
              margin: '6px 0 0', fontFamily: DISPLAY, fontSize: 'clamp(28px, 3.8vw, 46px)', fontWeight: 800,
              lineHeight: 1.02, letterSpacing: '-.035em', color: '#FFFFFF', minHeight: '1em',
            }}
          >
            {counts.isPending ? (
              <span
                className="mb-skel"
                aria-label="Loading the race name"
                style={{ display: 'block', width: '68%', height: '0.86em', borderRadius: 12 }}
              />
            ) : (raceTitle || 'This race')}
          </h1>

          {raceDetail && (
            <p
              title={raceDetail}
              style={{
                margin: '8px 0 0', fontSize: 14, lineHeight: 1.5, color: '#F3E6DC',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}
            >
              {raceDetail}
            </p>
          )}

          {/* NOT `counts.isError`. That query catches its own failure and falls
              back to counting the tabs one read at a time, so it SUCCEEDS with
              no summary rather than erroring. What matters to a reader is that
              the name and totals are missing, however that came about. */}
          {!counts.isPending && !summary && (
            <p style={{ margin: '10px 0 0', fontSize: 13, color: '#F2A58D' }}>
              The race name and totals could not be loaded. The results below are complete.
            </p>
          )}

          {summary && (
            <dl className="mb-hero-facts" style={{ display: 'flex', flexWrap: 'wrap', margin: '20px 0 0' }}>
              {[
                ['Results', countOf(summary.everyone), '#FFFFFF'],
                ['Finished', countOf(summary.completed), '#FFFFFF'],
                Number(summary.completed) > 0 && ['Winning', formatClock(summary.fastest_seconds), '#FF8A47'],
                Number(summary.completed) > 0 && summary.median_seconds && ['Median', formatClock(summary.median_seconds), '#FFFFFF'],
              ].filter(Boolean).map(([label, value, colour], i, all) => (
                <div
                  key={label}
                  style={i < all.length - 1
                    ? { paddingRight: 26, marginRight: 26, borderRight: '1px solid rgba(255,255,255,.14)' }
                    : undefined}
                >
                  <dt className="mb-fig-l" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: '#C9B3A3' }}>
                    {label}
                  </dt>
                  <dd className="mb-fig-v" style={{ ...mono, margin: '6px 0 0', fontFamily: DISPLAY, fontSize: 28, fontWeight: 800, lineHeight: 1, color: colour }}>
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {/* A way down to your own card, which on a big field sits below the
              podium and out of sight. Only when there is a place to jump to. */}
          {mine.data && myPlace >= 1 && (
            <button
              type="button"
              onClick={toMyResult}
              style={{
                marginTop: 18, display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44,
                padding: '0 18px', borderRadius: 14, border: '1px solid rgba(255,255,255,.28)',
                background: 'rgba(255,255,255,.06)', color: '#FFFFFF', fontSize: 15, fontWeight: 700, cursor: 'pointer',
              }}
            >
              You placed {ordinal(myPlace)}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
            </button>
          )}
        </div>
      </header>

      {/* Organizer only, and unlike the attach panel it stays once everyone has
          a result — which is exactly when there is something to publish. */}
      <OrganizerBoardLink
        api={apiHelper}
        eventActivityId={raceId}
        signedIn={user.userId !== null && user.userId !== undefined}
      />

      {/* Organizer only, and only when there is somebody left to attach. For
          everybody else this renders nothing at all. */}
      <OrganizerAttachPanel
        api={apiHelper}
        eventActivityId={raceId}
        signedIn={user.userId !== null && user.userId !== undefined}
      />

      {/* ── The rest of the event ──────────────────────────────────────
          A board is a way into the whole event, not a dead end on one race.
          A segmented control: the race being read is the raised segment. */}
      {siblings.length > 1 && (
        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <nav
            aria-label="Activities in this event"
            className="mb-acts"
            style={{
              display: 'inline-flex', gap: 4, padding: 5, borderRadius: 16, background: '#F6F2EE',
              maxWidth: '100%', boxSizing: 'border-box', flexWrap: 'wrap', justifyContent: 'center',
            }}
          >
            {siblings.map((race) => {
              const here = String(race.id) === String(raceId);
              return (
                <Link
                  key={race.id}
                  to={`/results/${race.id}`}
                  aria-current={here ? 'page' : undefined}
                  title={race.name}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 42, maxWidth: 280,
                    padding: '0 16px', borderRadius: 12, textDecoration: 'none', flex: '0 0 auto',
                    background: here ? '#fff' : 'transparent',
                    boxShadow: here ? '0 1px 3px rgba(36,23,8,.14)' : 'none',
                    color: here ? INK : MUTED, fontSize: 14, fontWeight: here ? 700 : 600,
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {splitRaceName(race.name)[0] || race.name}
                  </span>
                  {here && summary && (
                    <span style={{ ...mono, fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: SOFT, color: ACCENT }}>
                      {countOf(summary.everyone)}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* ── The podium ─────────────────────────────────────────────────
          A stage: the champion raised in the middle on the tallest step,
          second to the left and third to the right, the way a podium actually
          stands.

          results_published is what GATES this. Unpublish no longer deletes the
          award rows (see publishApi's header), so the rows survive a withdrawal
          and this flag is the only thing standing between them and the public
          board. Drop the check and withdrawn winners reappear. */}
      {summary?.results_published && podiumCards.length > 0 && (
        <div
          className="mb-stage"
          style={{
            paddingTop: 40, marginBottom: 22, borderBottom: `1px solid ${HAIR}`,
            background: 'radial-gradient(55% 70% at 50% 100%, rgba(255,106,26,.13) 0%, rgba(255,106,26,0) 70%)',
          }}
        >
          <section
            aria-label="Declared winners"
            ref={railRef}
            className="mb-podium"
            style={{
              display: 'flex', alignItems: 'flex-end', justifyContent: 'center', flexWrap: 'wrap',
              gap: 16, maxWidth: 800, margin: '0 auto',
            }}
          >
            {podiumCards.map(({ award, result }) => {
              const position = Number(award.position);
              const first = position === 1;
              const second = position === 2;
              const label = first ? 'Champion' : (second ? 'Runner-up' : 'Third');
              const isMe = Boolean(mine.data && result && String(result.id) === String(mine.data.id));
              const lifted = String(podiumOn) === String(award.id);
              return (
                <article
                  key={award.id}
                  // Hover lifts a column on desktop; a phone has no hover, so a
                  // tap does the same and a second tap puts it back.
                  className={`mb-pod${lifted ? ' is-on' : ''}`}
                  onClick={() => setPodiumOn((cur) => (String(cur) === String(award.id) ? null : award.id))}
                  style={{
                    position: 'relative', minWidth: 0,
                    flex: first ? '1.15 1 220px' : '1 1 200px',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                    // 2nd LEFT, CHAMPION MIDDLE, 3rd RIGHT. The cards arrive
                    // sorted 1,2,3; order places them on the stage.
                    order: first ? 2 : (second ? 1 : 3),
                  }}
                >
                  <div className="mb-pod-top" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  {first && (
                    <svg className="mb-crown" width="34" height="24" viewBox="0 0 34 24" aria-hidden="true" style={{ marginBottom: 6 }}>
                      <path d="M3 20 6 5l7 7 4-10 4 10 7-7 3 15z" fill={FLAME} />
                    </svg>
                  )}
                  <span
                    aria-hidden="true"
                    className="mb-pod-ava"
                    style={{
                      width: first ? 84 : 66, height: first ? 84 : 66, borderRadius: 999, flex: 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: first ? 26 : 21, fontWeight: 700,
                      background: first ? INK : (isMe ? ACCENT : (second ? '#FFE9DB' : '#fff')),
                      color: first || isMe ? '#fff' : INK,
                      boxShadow: first
                        ? '0 0 0 4px #fff, 0 0 0 7px #FFB48A'
                        : (isMe ? `0 0 0 4px #fff, 0 0 0 7px ${FLAME}` : `0 0 0 1px ${HAIR}, 0 0 0 6px ${SOFT}`),
                    }}
                  >
                    {initialsOf(award.user_name)}
                  </span>
                  {isMe && (
                    <span style={{ marginTop: -10, position: 'relative', fontSize: 10, fontWeight: 800, letterSpacing: '.08em', padding: '3px 8px', borderRadius: 7, background: INK, color: '#fff' }}>
                      YOU
                    </span>
                  )}
                  <span
                    style={{
                      marginTop: 12, fontSize: 10, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase',
                      padding: '4px 10px', borderRadius: 999, background: first ? ACCENT : SOFT,
                      color: first ? '#FFF6E9' : ACCENT,
                    }}
                  >
                    {label}
                  </span>
                  <div style={{ marginTop: 8, padding: '0 8px', fontFamily: DISPLAY, fontSize: first ? 20 : 17, fontWeight: 700, letterSpacing: '-.02em', color: INK }}>
                    {award.user_name}
                  </div>
                  <div style={{ ...mono, marginTop: 3, fontSize: 12, color: MUTED }}>
                    {[result?.bib && `Bib ${result.bib}`, award.category || 'Overall'].filter(Boolean).join(' · ')}
                  </div>

                  {/* ALWAYS DRAWN, even when the time is not to hand: a podium is
                      three columns in a row and must line up whatever is
                      missing. */}
                  <div style={{ marginTop: 6, minHeight: 22, display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 8 }}>
                    {result ? (
                      <>
                        <span style={{ ...mono, fontSize: 15, fontWeight: 700, color: INK }}>
                          {formatClock(result.duration_seconds)}
                        </span>
                        <span style={{ ...mono, fontSize: 12, color: MUTED }}>
                          {formatPace(result.duration_seconds, result.distance_meters)} /km
                        </span>
                      </>
                    ) : (
                      <span style={{ fontSize: 13, color: MUTED }}>Time not available</span>
                    )}
                  </div>
                  </div>

                  <div
                    aria-hidden="true"
                    className="mb-step"
                    style={{
                      width: '100%', marginTop: 12, boxSizing: 'border-box',
                      height: first ? 176 : (second ? 128 : 96),
                      borderRadius: '22px 22px 0 0', display: 'flex', justifyContent: 'center', paddingTop: 14,
                      background: first
                        ? 'linear-gradient(180deg, #FF7B33 0%, #FF6A1A 100%)'
                        : (second ? 'linear-gradient(180deg, #FFD3B8 0%, #FFE9DB 100%)' : SOFT),
                      boxShadow: first ? '0 -10px 40px -10px rgba(255,106,26,.5)' : 'none',
                    }}
                  >
                    <span style={{ fontFamily: DISPLAY, fontSize: first ? 60 : (second ? 46 : 38), fontWeight: 800, lineHeight: 1, color: first ? '#fff' : INK }}>
                      {award.position}
                    </span>
                  </div>

                  {/* The rail's timer, hidden by CSS on desktop. The fill is
                      scaled by the rotation hook, never by a CSS animation: the
                      bar has to stop where it stood when a thumb lands on it. */}
                  <span
                    aria-hidden="true"
                    className="mb-podium-track"
                    style={{
                      position: 'absolute', left: 0, right: 0, bottom: 0, height: 3,
                      background: 'rgba(36,23,8,.10)',
                    }}
                  >
                    <span
                      data-podium-fill=""
                      style={{
                        display: 'block', height: '100%', width: '100%',
                        transform: 'scaleX(0)', transformOrigin: 'left center',
                        background: first ? INK : ACCENT,
                      }}
                    />
                  </span>
                </article>
              );
            })}
          </section>
        </div>
      )}

      {/* Your result — one bar of figures under the stage. Position only when
          the race is ranked; time, pace and distance always, so the bar is
          never half-built. The meter only appears when there is a field to be
          ahead of. */}
      {mine.data && (
        <section
          id="my-result"
          aria-label="Your result"
          className="mb-yours"
          style={{
            ...card, borderColor: 'rgba(255,106,26,.30)', padding: '20px 24px 22px', marginBottom: 22,
            scrollMarginTop: 96,
            boxShadow: '0 1px 2px rgba(36,23,8,.04), 0 16px 36px -22px rgba(255,106,26,.40)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '18px 28px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: '1 1 260px', minWidth: 0 }}>
              <span aria-hidden="true" style={{ width: 52, height: 52, flex: 'none', borderRadius: 999, background: ACCENT, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700 }}>
                {initialsOf(mine.data.athlete_name || user.name)}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', color: ACCENT }}>YOUR RESULT</span>
                  {/* "Ahead of 100%" is a strange way to tell somebody they won.
                      The podium gets the word; everyone else gets the share of
                      the field they came ahead of. */}
                  {myPlace >= 1 && myPlace <= 3 ? (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 7, background: INK, color: '#fff' }}>
                      {PODIUM_WORD[myPlace]}
                    </span>
                  ) : ahead !== null && (
                    <span style={{ ...mono, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 7, background: SOFT, color: ACCENT }}>
                      Ahead of {ahead}%
                    </span>
                  )}
                </div>
                <div style={{ marginTop: 3, fontFamily: DISPLAY, fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1.15, color: INK }}>
                  {mine.data.athlete_name || user.name}
                </div>
                <div style={{ ...mono, marginTop: 3, fontSize: 12, color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {[mine.data.bib && `Bib ${mine.data.bib}`, raceTitle].filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>

            <dl className="mb-tiles" style={{ display: 'flex', gap: 30, flexWrap: 'wrap', margin: 0 }}>
              {[
                mine.data.position_overall && {
                  label: 'Position', value: String(mine.data.position_overall),
                  note: summary?.completed ? `/ ${countOf(summary.completed)}` : '', accent: true,
                },
                { label: 'Finish time', value: formatClock(mine.data.duration_seconds), note: '' },
                { label: 'Pace', value: formatPace(mine.data.duration_seconds, mine.data.distance_meters), note: '/km' },
                formatKm(mine.data.distance_meters) && { label: 'Distance', value: formatKm(mine.data.distance_meters), note: '' },
              ].filter(Boolean).map((tile) => (
                <div key={tile.label}>
                  <dt style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: MUTED }}>
                    {tile.label}
                  </dt>
                  <dd style={{ ...mono, margin: '4px 0 0', display: 'flex', alignItems: 'baseline', gap: 5 }}>
                    <span style={{ fontFamily: DISPLAY, fontSize: 28, fontWeight: 800, lineHeight: 1, letterSpacing: '-.03em', color: tile.accent ? ACCENT : INK }}>
                      {tile.value}
                    </span>
                    {tile.note && <span style={{ fontSize: 13, fontWeight: 600, color: MUTED }}>{tile.note}</span>}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {ahead !== null && (
            <div style={{ marginTop: 18 }}>
              <div style={{ height: 8, borderRadius: 999, background: '#F4EFEA', overflow: 'hidden' }}>
                <div style={{ width: `${ahead}%`, height: '100%', borderRadius: 999, background: FLAME }} />
              </div>
              <p style={{ ...mono, margin: '8px 0 0', fontSize: 12, color: MUTED }}>
                {mine.data.position_overall} of {countOf(summary.completed)} finishers
                {formatKm(mine.data.distance_meters) && ` · ${formatKm(mine.data.distance_meters)} recorded`}
              </p>
            </div>
          )}
        </section>
      )}

      {/* The shape of the field. Held back below a handful of finishers: three
          bars of height one say nothing a reader cannot already see in the
          table, and a chart that says nothing still asks to be read. */}
      {summary && Number(summary.completed) >= HISTOGRAM_MIN_FINISHERS && (
        <FinishSpread
          bars={histogramBars(bands.data, { mineSeconds: mine.data?.duration_seconds })}
          mineResult={mine.data}
          summary={summary}
        />
      )}

      {/* The leaderboard: heading, status tabs, search and table in one card. */}
      <section style={{ ...card, overflow: 'hidden' }}>
        <div className="mb-pad" style={{ padding: '22px 24px 0' }}>
          <h2 style={{ margin: 0, fontFamily: DISPLAY, fontSize: 24, fontWeight: 800, letterSpacing: '-.02em', color: INK }}>
            Leaderboard
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: MUTED }}>
            Every result for this race. Sorting happens inside a tab, so a withdrawal can never sit above a finisher.
          </p>
        </div>

        {/* One row, always. Wrapping put the tabs on two lines on a phone; they
            scroll sideways instead, and the scrollbar is hidden because the
            tabs themselves show there is more to the right. */}
        <div
          role="tablist"
          aria-label="Result status"
          className="mb-tabstrip mb-pad"
          style={{
            display: 'flex', gap: 26, flexWrap: 'nowrap', overflowX: 'auto',
            padding: '8px 24px 0', scrollbarWidth: 'none',
          }}
        >
          {tabs.map((t) => {
            const on = t.key === tabKey;
            const count = tabCounts[t.key];
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => pickTab(t.key)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 50, padding: '0 2px',
                  flex: '0 0 auto', whiteSpace: 'nowrap',
                  border: 0, borderBottom: `3px solid ${on ? FLAME : 'transparent'}`,
                  background: 'none', color: on ? INK : MUTED,
                  fontSize: 15, fontWeight: on ? 700 : 600, cursor: 'pointer',
                }}
              >
                {t.label}
                {count !== null && count !== undefined && (
                  <span style={{ ...mono, fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: on ? ACCENT : 'rgba(36,23,8,.06)', color: on ? '#fff' : MUTED }}>
                    {countOf(count)}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Find yourself. A results list is read for one name, and on a field
            of a thousand that name is pages down. The count says why this is
            here rather than leaving it to be guessed. */}
        <section
          aria-label="Find yourself in the field"
          className="mb-pad"
          style={{
            padding: '16px 24px', borderTop: `1px solid ${HAIR}`, display: 'flex', alignItems: 'center',
            gap: '10px 18px', flexWrap: 'wrap',
          }}
        >
          <form onSubmit={onSearch} style={{ display: 'flex', gap: 10, flex: '1 1 380px', minWidth: 0 }}>
            <div style={{ flex: '1 1 220px', minWidth: 0, position: 'relative', display: 'flex', alignItems: 'center' }}>
              <svg
                width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={MUTED}
                strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"
                style={{ position: 'absolute', left: 14 }}
              >
                <circle cx="11" cy="11" r="6.5" />
                <path d="M16 16l4 4" />
              </svg>
              <label htmlFor="board-search" style={srOnly}>
                Search by name or bib number
              </label>
              <input
                id="board-search"
                type="search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  // Emptying the box — including by the input's own X — puts the
                  // whole field back at once. Waiting for Search to be pressed
                  // left the visitor looking at a filtered list they had cleared.
                  if (e.target.value === '') {
                    setTerm('');
                    setPage(1);
                  }
                }}
                placeholder="Search by name or bib number"
                style={{
                  width: '100%', minHeight: 46, padding: '0 14px 0 42px', borderRadius: 12,
                  border: '1px solid rgba(36,23,8,.16)', background: '#fff', fontSize: 15,
                  color: INK, boxSizing: 'border-box',
                }}
              />
            </div>
            <button
              type="submit"
              style={{
                minHeight: 46, padding: '0 20px', border: 0, borderRadius: 12, background: INK,
                color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', flex: 'none',
              }}
            >
              Search
            </button>
          </form>
          <p style={{ ...mono, margin: 0, fontSize: 13, color: MUTED }}>
            <strong style={{ color: INK }}>{countOf(sorted.length)}</strong> {sorted.length === 1 ? 'result' : 'results'}
            {pageCount > 1 && <> across {pageCount} pages — don&rsquo;t scroll for it</>}
          </p>
        </section>

        {/* Loading, error and empty are three different things and used to be
            one. On a failed read the loader span forever: no message, no retry,
            nothing to tell a visitor the page had given up. */}
        {results.isPending && !results.isError && <BoardLoader />}

        {results.isError && (
          <section
            role="alert"
            style={{
              border: `1px solid rgba(194,65,12,.3)`, background: '#FFF1E8', borderRadius: 18,
              padding: '22px 24px', margin: '0 24px 24px',
            }}
          >
            <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#8A2C06' }}>
              These results could not be loaded.
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 14, color: '#8A2C06', lineHeight: 1.6 }}>
              The race is there — the list of finishers did not come back. This is
              usually a connection that dropped.
            </p>
            <button
              type="button"
              onClick={() => results.refetch()}
              disabled={results.isFetching}
              style={{
                marginTop: 16, minHeight: 46, padding: '0 20px', border: 0, borderRadius: 11,
                background: results.isFetching ? '#D8CEC3' : INK, color: '#fff',
                fontSize: 14, fontWeight: 700, cursor: results.isFetching ? 'not-allowed' : 'pointer',
              }}
            >
              {results.isFetching ? 'Trying again…' : 'Try again'}
            </button>
          </section>
        )}

        {!results.isPending && !results.isError && sorted.length === 0 && (
          <div style={{ padding: '56px 24px', textAlign: 'center', borderTop: `1px solid ${HAIR}` }}>
            <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: INK }}>{empty.title}</p>
            <p style={{ margin: '8px auto 0', maxWidth: 440, fontSize: 14, lineHeight: 1.6, color: MUTED }}>{empty.body}</p>
          </div>
        )}

        {!results.isPending && !results.isError && sorted.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="mb-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                <thead>
                  <tr>
                    <th scope="col" style={{ ...th, width: 80, paddingLeft: 24 }}>Rank</th>
                    <th scope="col" style={th}>Participant</th>
                    {[{ key: 'distance_meters', label: 'Distance' }, { key: 'duration_seconds', label: 'Time' }].map((col) => {
                      const on = canSort && activeSort.column === col.key;
                      return (
                        <th
                          key={col.key}
                          scope="col"
                          aria-sort={on ? (activeSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                          style={{ ...th, textAlign: 'right', width: 140, paddingRight: col.key === 'duration_seconds' ? 24 : 14 }}
                        >
                          {canSort ? (
                            <button
                              type="button"
                              onClick={() => pickSort(col.key)}
                              style={{
                                minHeight: 44, border: 0, background: 'none', cursor: 'pointer', padding: 0,
                                font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit',
                                color: on ? ACCENT : MUTED, display: 'inline-flex', alignItems: 'center', gap: 4,
                              }}
                            >
                              {col.label}
                              <span aria-hidden="true" style={{ opacity: on ? 1 : 0.35 }}>
                                {on ? (activeSort.direction === 'asc' ? '↑' : '↓') : '↕'}
                              </span>
                            </button>
                          ) : col.label}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row, index) => {
                    // Your own row, marked where the eye actually goes: a reader
                    // scanning the field reads the TABLE, and there every row
                    // looked the same — so people scrolled past themselves.
                    const isMine = mine.data && String(row.id) === String(mine.data.id);
                    const rank = (safePage - 1) * ROWS_PER_PAGE + index + 1;
                    const medal = canSort && rank <= 3 ? rank : 0;
                    const cell = isMine ? { ...td, background: SOFT } : td;
                    return (
                      <tr key={row.id} className="mb-row">
                        <td style={{ ...cell, paddingLeft: 24, boxShadow: isMine ? `inset 4px 0 0 ${FLAME}` : 'none' }}>
                          {canSort ? (
                            <span
                              style={{
                                ...mono, minWidth: 36, height: 36, padding: '0 6px', boxSizing: 'border-box',
                                borderRadius: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                fontFamily: DISPLAY, fontSize: 15, fontWeight: 800,
                                background: ({ 1: ACCENT, 2: '#FFD3B8', 3: '#FFE9DB' })[medal] || '#F6F2EE',
                                color: medal === 1 ? '#fff' : (medal ? INK : MUTED),
                              }}
                            >
                              {rank}
                            </span>
                          ) : <span style={{ color: MUTED }}>—</span>}
                        </td>
                        {/* The name is the way into the run behind the result —
                            its route, splits and climb. A link in the cell rather
                            than a click handler on the <tr>: a row is not
                            focusable, so a handler there would be unreachable by
                            keyboard and invisible to a screen reader. */}
                        <td style={{ ...cell, color: INK, fontSize: 15 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flexWrap: 'wrap' }}>
                            <span
                              aria-hidden="true"
                              className="mb-avatar"
                              style={{
                                width: 36, height: 36, flex: 'none', borderRadius: 999, display: 'inline-flex',
                                alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                                background: isMine ? ACCENT : '#F6F2EE', color: isMine ? '#fff' : '#4A3D33',
                              }}
                            >
                              {initialsOf(row.athlete_name)}
                            </span>
                            <Link
                              to={`/results/${eventActivityId}/${row.id}`}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                minHeight: 44, color: INK, fontWeight: 700, textDecoration: 'none',
                              }}
                            >
                              {row.athlete_name || 'Athlete'}
                              <span aria-hidden="true" style={{ color: ACCENT, fontWeight: 700 }}>›</span>
                            </Link>
                            {isMine && (
                              <span style={{ padding: '3px 8px', borderRadius: 7, background: ACCENT, color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: '.1em' }}>
                                YOU
                              </span>
                            )}
                            {row.bib ? (
                              <span style={{ ...mono, fontSize: 12, padding: '3px 8px', borderRadius: 8, background: '#F6F2EE', color: '#4A3D33' }}>
                                Bib {row.bib}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td style={{ ...cell, ...mono, textAlign: 'right', color: '#4A3D33' }}>{formatKm(row.distance_meters)}</td>
                        <td style={{ ...cell, ...mono, textAlign: 'right', paddingRight: 24, fontFamily: DISPLAY, fontSize: 16, fontWeight: 800, color: INK }}>
                          {formatClock(row.duration_seconds)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Only when your row is NOT already on screen. Pinning it regardless
                put the winner's own row underneath the people they had just
                beaten, which reads as another finisher. */}
            {mine.data && !visible.some((row) => String(row.id) === String(mine.data.id)) && (
              <div className="mb-pad" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0 24px', minHeight: 68, background: SOFT, borderTop: `2px solid ${FLAME}`, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', padding: '4px 9px', borderRadius: 7, background: ACCENT, color: '#fff' }}>YOU</span>
                <span style={{ flex: 1, minWidth: 120, fontSize: 15, fontWeight: 700, color: INK }}>
                  {mine.data.athlete_name || user.name}
                </span>
                <span style={{ ...mono, fontSize: 14, color: '#4A3D33' }}>{formatKm(mine.data.distance_meters)}</span>
                <span style={{ ...mono, fontFamily: DISPLAY, fontSize: 17, fontWeight: 800, color: ACCENT }}>{formatClock(mine.data.duration_seconds)}</span>
              </div>
            )}

            <nav
              aria-label="Result pages"
              className="mb-pad"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: '16px 24px', flexWrap: 'wrap', borderTop: `1px solid ${HAIR}`, background: '#FBF8F5' }}
            >
              <p style={{ ...mono, margin: 0, fontSize: 14, color: MUTED }}>
                Showing <strong style={{ color: INK }}>{countOf((safePage - 1) * ROWS_PER_PAGE + 1)}–{countOf(Math.min(safePage * ROWS_PER_PAGE, sorted.length))}</strong> of <strong style={{ color: INK }}>{countOf(sorted.length)}</strong>
                {results.data?.truncated ? ` (first ${BOARD_MAX_ROWS.toLocaleString()})` : ''}
              </p>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="mb-pg"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  aria-label="Previous page"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 42, padding: '0 14px',
                    borderRadius: 12, border: `1px solid ${HAIR}`, background: '#fff',
                    color: safePage <= 1 ? '#A69683' : INK, fontWeight: 600, fontSize: 14,
                    cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
                  <span className="mb-pg-word">Previous</span>
                </button>
                {pageCount > 1 && pageWindow(safePage, pageCount).map((n, i) => (n === null ? (
                  <span key={`gap-${i}`} aria-hidden="true" style={{ width: 24, textAlign: 'center', color: MUTED, fontWeight: 700 }}>…</span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    className="mb-pg"
                    onClick={() => setPage(n)}
                    aria-current={n === safePage ? 'page' : undefined}
                    aria-label={`Page ${n}`}
                    style={{
                      ...mono, minWidth: 42, minHeight: 42, padding: '0 10px', borderRadius: 12,
                      border: `1px solid ${n === safePage ? INK : HAIR}`,
                      background: n === safePage ? INK : '#fff', color: n === safePage ? '#fff' : INK,
                      fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    {n}
                  </button>
                )))}
                <button
                  type="button"
                  className="mb-pg"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={safePage >= pageCount}
                  aria-label="Next page"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 42, padding: '0 14px',
                    borderRadius: 12, border: `1px solid ${HAIR}`, background: '#fff',
                    color: safePage >= pageCount ? '#A69683' : INK, fontWeight: 600, fontSize: 14,
                    cursor: safePage >= pageCount ? 'not-allowed' : 'pointer',
                  }}
                >
                  <span className="mb-pg-word">Next</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
                </button>
              </div>
            </nav>
          </>
        )}
      </section>
    </main>
  );
}
